// src/components/PresenceIndicator.js
// Shows a live green dot when Sky Bri is logged into the admin panel
// All subscribers see this on their dashboard in real time
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import './PresenceIndicator.css'

const SKY_BRI_ID = process.env.REACT_APP_ADMIN_ID

export default function PresenceIndicator({ size = 'md' }) {
  const [isOnline, setIsOnline] = useState(false)
  const [lastSeen, setLastSeen] = useState(null)

  useEffect(() => {
    // Get initial presence
    fetchPresence()

    // Subscribe to real-time presence changes
    const sub = supabase
      .channel('sky-bri-presence')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'presence',
        filter: `id=eq.${SKY_BRI_ID}`
      }, payload => {
        setIsOnline(payload.new.is_online)
        setLastSeen(payload.new.last_seen)
      })
      .subscribe()

    return () => sub.unsubscribe()
  }, [])

  async function fetchPresence() {
    const { data } = await supabase
      .from('presence')
      .select('is_online, last_seen')
      .eq('id', SKY_BRI_ID)
      .single()
    if (data) {
      setIsOnline(data.is_online)
      setLastSeen(data.last_seen)
    }
  }

  function getLastSeenText() {
    if (!lastSeen) return ''
    const diff = Date.now() - new Date(lastSeen).getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 5) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  return (
    <div className={`presence presence--${size}`}>
      <div className={`presence__dot ${isOnline ? 'presence__dot--online' : 'presence__dot--offline'}`}>
        {isOnline && <div className="presence__pulse" />}
      </div>
      <span className="presence__label">
        {isOnline ? 'Sky Bri is online' : `Last seen ${getLastSeenText()}`}
      </span>
    </div>
  )
}

// Hook for admin to update their own presence
export function useAdminPresence(isAdmin) {
  useEffect(() => {
    if (!isAdmin) return

    const SKY_ID = process.env.REACT_APP_ADMIN_ID

    // Set online
    supabase.from('presence').upsert({
      id: SKY_ID,
      is_online: true,
      last_seen: new Date().toISOString()
    }).then()

    // Heartbeat every 30 seconds
    const interval = setInterval(() => {
      supabase.from('presence').upsert({
        id: SKY_ID,
        is_online: true,
        last_seen: new Date().toISOString()
      }).then()
    }, 30000)

    // Set offline when leaving
    function handleOffline() {
      supabase.from('presence').upsert({
        id: SKY_ID,
        is_online: false,
        last_seen: new Date().toISOString()
      }).then()
    }

    window.addEventListener('beforeunload', handleOffline)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) handleOffline()
      else supabase.from('presence').upsert({
        id: SKY_ID,
        is_online: true,
        last_seen: new Date().toISOString()
      }).then()
    })

    return () => {
      clearInterval(interval)
      handleOffline()
      window.removeEventListener('beforeunload', handleOffline)
    }
  }, [isAdmin])
}
